import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { COMPANY } from "../data";
import { LOGO_URL } from "../config";
import { GlobalSearch, prefocusKeyboard, prefetchProducts } from "./GlobalSearch";
import { useLang } from "../i18n";

const LangToggle = ({ mobile = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid={mobile ? "lang-toggle-mobile" : "lang-toggle"}
      className={`flex items-center gap-0.5 border border-white/20 rounded-full ${mobile ? "p-0.5" : "p-1"}`}
    >
      {["tr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`${mobile ? "mobile-" : ""}lang-btn-${l}`}
          onClick={() => setLang(l)}
          className={`${mobile ? "px-2 py-1 text-[9px]" : "px-2.5 py-1 text-[10px]"} rounded-full font-sans uppercase tracking-[0.12em] transition-colors duration-300 ${
            lang === l ? "bg-[#7BA05B] text-[#0D0C0B]" : "text-[#F2EFEB] hover:text-[#7BA05B]"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const kaplamaSubLinks = [
  { to: "/kaplama", label: "Doğal Kaplama", testid: "nav-sub-dogal" },
  { to: "/kaplama/kok", label: "Kök Kaplama", testid: "nav-sub-kok" },
  { to: "/kaplama/piramit", label: "Piramit Kaplama", testid: "nav-sub-piramit" },
  { to: "/kaplama/papel", label: "Papel Kaplama", testid: "nav-sub-papel" },
];
const yapaySubLinks = [
  { to: "/kaplama-yapay/normal", label: "Normal (Yapay)", testid: "nav-sub-normal" },
  { to: "/kaplama-yapay/3d", label: "3D Yüzey", testid: "nav-sub-3d" },
];
const pageLinks = [
  { to: "/", label: "Ana Sayfa", testid: "nav-link-anasayfa", home: true },
  { section: "hakkimizda", label: "Hakkımızda", testid: "nav-link-hakkimizda" },
  { to: "/kaplama-yapay", label: "Yapay Kaplama", testid: "nav-link-yapay", subLinks: yapaySubLinks },
  { to: "/kaplama", label: "Kaplama", testid: "nav-link-kaplama", subLinks: kaplamaSubLinks },
  { to: "/kereste", label: "Kereste", testid: "nav-link-kereste" },
  { to: "/vinterio", label: "Vinterio", testid: "nav-link-vinterio" },
  { to: "/linea", label: "Linea", testid: "nav-link-linea" },
  { to: "/boyali-kaplama", label: "Boyalı Kaplama", testid: "nav-link-boyali" },
  { to: "/kontrplak", label: "Kontrplak", testid: "nav-link-kontrplak" },
  { to: "/sertifikalar", label: "Sertifikalar", testid: "nav-link-sertifikalar" },
];
const sectionLinks = [];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const linkColor = scrolled ? "text-[#2F4A26] hover:text-[#7BA05B]" : "text-[#F2EFEB] hover:text-[#7BA05B]";

  const [pastHero, setPastHero] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lenis = useLenis();

  useEffect(() => { prefetchProducts(); }, []);
  const { t, dn } = useLang();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      setPastHero(window.scrollY > window.innerHeight * 0.55);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const forceTop = () => {
    let active = true;
    const cancel = () => { active = false; };
    const step = () => {
      if (!active) return;
      if (window.scrollY > 4) {
        lenis ? lenis.scrollTo(0, { immediate: true, force: true }) : window.scrollTo(0, 0);
      }
    };
    window.addEventListener("wheel", cancel, { passive: true });
    window.addEventListener("touchstart", cancel, { passive: true });
    const iv = setInterval(step, 60);
    setTimeout(() => {
      active = false;
      clearInterval(iv);
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
    }, 1000);
    step();
  };

  const goHome = () => {
    if (location.pathname === "/") {
      forceTop();
    } else {
      navigate("/");
      setTimeout(forceTop, 0);
    }
  };

  const goSection = (id) => {
    const headerOffset = window.innerWidth >= 1024 ? 128 : 104;
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      if (lenis) {
        lenis.scrollTo(top, { duration: 1.1 });
      } else {
        window.scrollTo({ top, behavior: "smooth" });
      }
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "pt-1 pb-3 backdrop-blur-md bg-white/95 border-b border-black/10 shadow-[0_2px_20px_rgba(0,0,0,0.06)]" : "py-7"
        }`}
      >
        <nav className="w-full max-w-screen-2xl 2xl:max-w-[1800px] mx-auto pl-2 pr-3 md:pl-3 md:pr-4 xl:pr-5 2xl:pr-8 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            onClick={() => {
              if (location.pathname === "/") {
                if (lenis) lenis.scrollTo(0, { duration: 1 });
                else window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="flex items-center gap-3 group transition-all duration-500 shrink-0"
          >
            <img
              src={LOGO_URL}
              alt="İnter Orman Ürünleri logo"
              className={`w-auto transition-all duration-500 ${scrolled ? "h-8 md:h-9" : "h-9 md:h-11"}`}
              data-testid="nav-logo-img"
            />
            <span className={`font-serif text-base md:text-lg xl:text-[0.95rem] 2xl:text-lg tracking-tight group-hover:text-[#C67D57] transition-colors whitespace-nowrap ${scrolled ? "text-[#1A1715]" : "text-[#F2EFEB]"}`}>
              İnter Orman Ürünleri
            </span>
          </Link>

          <div className="hidden xl:flex flex-1 min-w-0 items-center justify-end gap-2 xl:gap-3 2xl:gap-4 min-[1800px]:gap-5 ml-3 xl:ml-4">
            {pageLinks.map((l) =>
              l.subLinks ? (
                <div key={l.to} className="relative group">
                  <span
                    data-testid={l.testid}
                    className={`flex items-center gap-1 whitespace-nowrap cursor-default font-sans uppercase tracking-[0.05em] xl:text-[10px] 2xl:text-[11px] 2xl:tracking-[0.1em] min-[1800px]:text-xs min-[1800px]:tracking-[0.2em] ${scrolled ? "text-[#2F4A26]" : "text-[#F2EFEB]"} group-hover:text-[#7BA05B] transition-colors duration-300`}
                  >
                    {dn(l.label)}
                    <ChevronDown size={13} strokeWidth={1.5} className="mt-px transition-transform duration-300 group-hover:rotate-180" />
                  </span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                    <div className="bg-white border border-black/10 rounded-xl p-2 min-w-[200px] shadow-2xl shadow-black/15">
                      {l.subLinks.map((s) => (
                        <Link
                          key={s.testid}
                          to={s.to}
                          data-testid={s.testid}
                          className="block px-4 py-3 rounded-lg font-sans uppercase tracking-[0.15em] text-[11px] text-[#2F4A26] hover:text-[#7BA05B] hover:bg-[#2F4A26]/5 transition-colors duration-200"
                        >
                          {dn(s.label)}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : l.home ? (
                <button
                  key="home-desktop"
                  data-testid={l.testid}
                  onClick={goHome}
                  className={`whitespace-nowrap font-sans uppercase tracking-[0.05em] xl:text-[10px] 2xl:text-[11px] 2xl:tracking-[0.1em] min-[1800px]:text-xs min-[1800px]:tracking-[0.2em] ${linkColor} transition-colors duration-300`}
                >
                  {dn(l.label)}
                </button>
              ) : l.section ? (
                <button
                  key={l.section}
                  data-testid={l.testid}
                  onClick={() => goSection(l.section)}
                  className={`whitespace-nowrap font-sans uppercase tracking-[0.05em] xl:text-[10px] 2xl:text-[11px] 2xl:tracking-[0.1em] min-[1800px]:text-xs min-[1800px]:tracking-[0.2em] ${linkColor} transition-colors duration-300`}
                >
                  {dn(l.label)}
                </button>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={`whitespace-nowrap font-sans uppercase tracking-[0.05em] xl:text-[10px] 2xl:text-[11px] 2xl:tracking-[0.1em] min-[1800px]:text-xs min-[1800px]:tracking-[0.2em] ${linkColor} transition-colors duration-300`}
                >
                  {dn(l.label)}
                </Link>
              )
            )}
            {sectionLinks.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => goSection(l.id)}
                className={`whitespace-nowrap font-sans uppercase tracking-[0.05em] xl:text-[10px] 2xl:text-[11px] 2xl:tracking-[0.1em] min-[1800px]:text-xs min-[1800px]:tracking-[0.2em] ${linkColor} transition-colors duration-300`}
              >
                {dn(l.label)}
              </button>
            ))}
            <AnimatePresence>
              {(
                <motion.button
                  key="nav-search"
                  data-testid="nav-search-btn"
                  onClick={() => { prefocusKeyboard(); setSearchOpen(true); }}
                  aria-label={t("nav.searchAria")}
                  initial={{ opacity: 0, x: 42, rotateY: 55, scale: 0.92 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 42, rotateY: 55, scale: 0.92 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformPerspective: 700, transformOrigin: "right center" }}
                  className={`group flex items-center gap-2 xl:gap-2 border hover:border-[#7BA05B] rounded-full px-3 xl:px-3 2xl:px-4 py-2.5 hover:text-[#7BA05B] transition-colors duration-300 shrink-0 ${scrolled ? "border-[#2F4A26]/30 text-[#2F4A26]" : "border-white/20 text-[#F2EFEB]"}`}
                >
                  <Search size={15} strokeWidth={1.75} className="shrink-0 group-hover:text-[#7BA05B] transition-colors" />
                  <span className={`h-px w-8 xl:w-6 2xl:w-10 min-[1800px]:w-16 group-hover:bg-[#7BA05B]/60 transition-colors ${scrolled ? "bg-[#2F4A26]/30" : "bg-white/25"}`} />
                </motion.button>
              )}
            </AnimatePresence>
            <LangToggle />
            <button
              data-testid="nav-cta-contact"
              onClick={() => goSection("iletisim")}
              className={`whitespace-nowrap font-sans uppercase tracking-[0.05em] xl:text-[10px] 2xl:text-[11px] 2xl:tracking-[0.1em] min-[1800px]:text-xs min-[1800px]:tracking-[0.2em] px-3.5 xl:px-4 2xl:px-5 min-[1800px]:px-6 py-3 rounded-full transition-all duration-300 shrink-0 ${scrolled ? "bg-[#2F4A26] text-[#F2EFEB] hover:bg-[#7BA05B]" : "bg-[#F2EFEB] text-[#0D0C0B] hover:bg-[#C67D57] hover:text-[#F2EFEB]"}`}
            >
              {t("nav.contact")}
            </button>
          </div>

          <div className="xl:hidden flex flex-1 min-w-0 items-center justify-end gap-2.5 min-[400px]:gap-3 ml-3">
            <AnimatePresence>
              {(
                <motion.button
                  key="nav-search-mobile"
                  data-testid="nav-search-btn-mobile"
                  aria-label={t("nav.searchAria")}
                  onClick={() => { prefocusKeyboard(); setSearchOpen(true); }}
                  initial={{ opacity: 0, x: 42, rotateY: 55, scale: 0.92 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 42, rotateY: 55, scale: 0.92 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformPerspective: 700, transformOrigin: "right center" }}
                  className={`flex flex-1 min-w-0 max-w-[190px] md:max-w-[240px] lg:max-w-[280px] items-center gap-2 border rounded-full px-3.5 md:px-4 py-2.5 ${scrolled ? "border-[#2F4A26]/30 text-[#2F4A26]" : "border-white/20 text-[#F2EFEB]"}`}
                >
                  <Search size={18} strokeWidth={1.75} className="shrink-0" />
                  <span className={`h-px flex-1 ${scrolled ? "bg-[#2F4A26]/30" : "bg-white/25"}`} />
                </motion.button>
              )}
            </AnimatePresence>
            <LangToggle mobile />
            <button
              data-testid="nav-mobile-toggle"
              className={scrolled ? "text-[#2F4A26]" : "text-[#F2EFEB]"}
              onClick={() => { setOpen(true); setOpenSub(null); }}
            >
              <Menu size={34} strokeWidth={1.75} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-white flex flex-col p-8 overflow-y-auto"
          >
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-3 font-serif text-xl text-[#1A1715]">
                <img src={LOGO_URL} alt="logo" className="h-9 w-auto" />
                {COMPANY.name}
              </span>
              <div className="flex items-center gap-3">
                <button
                  data-testid="mobile-menu-close"
                  onClick={() => { setOpen(false); setOpenSub(null); }}
                  aria-label="Menüyü kapat"
                  className="w-10 h-10 rounded-full border border-[#2F4A26]/30 flex items-center justify-center"
                >
                  <X strokeWidth={1.5} className="text-[#2F4A26]" size={20} />
                </button>
              </div>
            </div>
            <div className="flex flex-col mt-8 pb-16">
              <motion.button
                data-testid="mobile-search-btn"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.05 }}
                onClick={() => { setOpen(false); setOpenSub(null); prefocusKeyboard(); setSearchOpen(true); }}
                className="flex items-center gap-3 w-full bg-[#2F4A26]/5 border border-[#2F4A26]/25 rounded-full px-5 py-3.5 text-left mb-6"
              >
                <Search size={18} strokeWidth={1.75} className="text-[#2F4A26]" />
                <span className="font-sans uppercase tracking-[0.2em] text-xs text-[#2F4A26]">{t("nav.searchProducts")}</span>
              </motion.button>
              {pageLinks.map((l, i) => (
                <motion.div
                  key={l.to || l.section}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.08 + i * 0.05 }}
                  className="border-b border-black/10"
                >
                  {l.subLinks ? (
                    <button
                      data-testid={`mobile-${l.testid}`}
                      onClick={() => setOpenSub(openSub === l.testid ? null : l.testid)}
                      className="w-full flex items-center justify-between font-serif text-2xl text-left text-[#1A1715] py-4"
                    >
                      {dn(l.label)}
                      <ChevronDown
                        strokeWidth={1.5}
                        className={`text-[#2F4A26] transition-transform duration-300 ${openSub === l.testid ? "rotate-180" : ""}`}
                      />
                    </button>
                  ) : l.home ? (
                    <button
                      data-testid={`mobile-${l.testid}`}
                      onClick={() => { setOpen(false); goHome(); }}
                      className="block w-full font-serif text-2xl text-left text-[#1A1715] py-4"
                    >
                      {dn(l.label)}
                    </button>
                  ) : l.section ? (
                    <button
                      data-testid={`mobile-${l.testid}`}
                      onClick={() => { setOpen(false); goSection(l.section); }}
                      className="block w-full font-serif text-2xl text-left text-[#1A1715] py-4"
                    >
                      {dn(l.label)}
                    </button>
                  ) : (
                    <Link
                      data-testid={`mobile-${l.testid}`}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="block w-full font-serif text-2xl text-left text-[#1A1715] py-4"
                    >
                      {dn(l.label)}
                    </Link>
                  )}
                  {l.subLinks && (
                    <AnimatePresence initial={false}>
                      {openSub === l.testid && (
                        <motion.div
                          key="sub"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col gap-1 mb-4 pl-4 border-l-2 border-[#2F4A26]/30">
                            {l.subLinks.map((s) => (
                              <Link
                                key={s.testid}
                                data-testid={`mobile-${s.testid}`}
                                to={s.to}
                                onClick={() => { setOpen(false); setOpenSub(null); }}
                                className="block w-full py-2 font-sans uppercase tracking-[0.15em] text-sm text-[#2F4A26]"
                              >
                                {dn(s.label)}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </motion.div>
              ))}
              {sectionLinks.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.26 + i * 0.05 }}
                  onClick={() => { setOpen(false); goSection(l.id); }}
                  className="font-serif text-2xl text-left text-[#1A1715] py-4 border-b border-black/10"
                >
                  {dn(l.label)}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-nav-link-iletisim"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                onClick={() => { setOpen(false); goSection("iletisim"); }}
                className="mt-8 inline-block text-center font-sans uppercase tracking-[0.2em] text-sm text-[#F2EFEB] bg-[#2F4A26] px-8 py-4 rounded-full"
              >
                {t("nav.contact")}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};
